import { Injectable } from "@angular/core";
import { Observable, of, BehaviorSubject } from "rxjs";
import { map, catchError, shareReplay, first } from "rxjs/operators";
import { GlobalService } from "./global.services";
import { localStorageKey } from "@core/constants";

interface PermissionConfig {
  include?: string[];
  allow?: string[];
  visible?: boolean;
  enable?: boolean;
}

type PermissionValidationParam =
  | { [key: string]: string[] | PermissionConfig }
  | null
  | undefined;

@Injectable({
  providedIn: "root",
})
export class PermissionValidationService {
  private readonly PARAMETER_KEY = "TURBO-WMS_USERS-VALIDATION";
  private validationParam$: Observable<PermissionValidationParam>;
  private validationParamCache: PermissionValidationParam | null = null;
  private isLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(private globalService: GlobalService) {
    this.validationParam$ = this.loadValidationParameter().pipe(
      shareReplay(1)
    );
  }

  private loadValidationParameter(): Observable<PermissionValidationParam> {
    return this.globalService.getParameterValue(this.PARAMETER_KEY).pipe(
      map((response: any) => {
        try {
          return typeof response === "string" ? JSON.parse(response) : response;
        } catch {
          return null;
        }
      }),
      catchError(() => of(null))
    );
  }

  private getUserEmail(): string | null {
    try {
      const data = localStorage.getItem(localStorageKey);
      if (!data) return null;
      const parsed = JSON.parse(data);
      return parsed?.user?.email || null;
    } catch {
      return null;
    }
  }

  private normalizeConfig(
    _: string,
    config: string[] | PermissionConfig
  ): PermissionConfig {
    return Array.isArray(config) ? { include: config } : config;
  }

  private getPermissionConfig(permission: string): PermissionConfig | null {
    if (!this.validationParamCache) return null;

    const config = this.validationParamCache[permission];
    if (!config) return null;

    return this.normalizeConfig(permission, config);
  }

  private hasUserAccess(
    config: PermissionConfig,
    userEmail: string,
    userHasPermission: boolean
  ): boolean {
    const hasInclude = config.include && config.include.length > 0;
    const hasAllow = config.allow && config.allow.length > 0;

    if (hasInclude && hasAllow) {
      const inInclude = config.include!.includes(userEmail) && userHasPermission;
      const inAllow = config.allow!.includes(userEmail);
      return inInclude || inAllow;
    }

    if (hasInclude) {
      return config.include!.includes(userEmail) && userHasPermission;
    }

    if (hasAllow) {
      const inAllow = config.allow!.includes(userEmail);
      return userHasPermission || inAllow;
    }

    return userHasPermission;
  }

  canAccess(permission: string, userHasPermission: boolean): Observable<boolean> {
    if (!this.isLoaded$.value && this.validationParamCache === null) {
      return this.isLoaded$.pipe(
        first((loaded) => loaded),
        map(() => this.canAccessSync(permission, userHasPermission))
      );
    }

    return of(this.canAccessSync(permission, userHasPermission));
  }

  canAccessSync(permission: string, userHasPermission: boolean): boolean {
    const config = this.getPermissionConfig(permission);

    if (!config) return userHasPermission;

    if (config.enable === false) {
      return false;
    }

    const userEmail = this.getUserEmail();
    if (!userEmail) return false;

    return this.hasUserAccess(config, userEmail, userHasPermission);
  }

  isVisibleInMenu(
    permission: string,
    userHasPermission: boolean
  ): Observable<boolean> {
    if (!this.isLoaded$.value && this.validationParamCache === null) {
      return this.isLoaded$.pipe(
        first((loaded) => loaded),
        map(() => this.isVisibleInMenuSync(permission, userHasPermission))
      );
    }

    return of(this.isVisibleInMenuSync(permission, userHasPermission));
  }

  isVisibleInMenuSync(
    permission: string,
    userHasPermission: boolean
  ): boolean {
    const config = this.getPermissionConfig(permission);
    const userEmail = this.getUserEmail();

    if (!config) {
      return userHasPermission;
    }

    if (config.enable === false || config.visible === false) {
      return false;
    }

    if (!userEmail) {
      return false;
    }

    return this.hasUserAccess(config, userEmail, userHasPermission);
  }

  initialize(): void {
    this.validationParam$.subscribe((param) => {
      this.validationParamCache = param;
      this.isLoaded$.next(true);
    });
  }

  waitForLoad(): Observable<boolean> {
    if (this.isLoaded$.value) {
      return of(true);
    }
    return this.isLoaded$.pipe(first((loaded) => loaded));
  }
}